import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import type { ScenarioCompanion, ScenarioCompanionState } from "@/api/types";

/**
 * 엔딩 카드 아래, 보상 섹션 위에 삽입하는 "회수록" 컴포넌트.
 *
 * <p>2026-04-23: 사용자 피드백 "fallen 동료를 엔딩 카드/에필로그에도 더 직접 남기기"
 * 대응. 런 종료 시 아직 살아 있는 인원, 부상인, 쓰러진 이름을 분리해서 각인한다.
 *
 * <p>구성 (companions 가 있는 시나리오 전용 — 현재 frontier-vanguard):
 * <ol>
 *   <li>쓰러진 이름 (priority high): 초상/이름/역할/쓰러진 주차/짧은 추모 한 줄</li>
 *   <li>부상 이름 (priority mid): 이름만 그룹으로</li>
 *   <li>살아 돌아온 이름 (priority low): 이름만 그룹으로</li>
 * </ol>
 *
 * <p>companions 비어 있으면 컴포넌트 자체 미렌더. fallen=0 이면 "모두 데리고
 * 돌아왔다" 긍정 섹션만.
 */
interface FallenCompanionsMemorialProps {
    companions: ScenarioCompanion[];
    companionStates: ScenarioCompanionState[];
    maxWeeks: number;
}

export function FallenCompanionsMemorial({
    companions,
    companionStates,
    maxWeeks,
}: FallenCompanionsMemorialProps) {
    if (!companions || companions.length === 0) return null;

    const stateById = new Map(companionStates.map(s => [s.companionId, s]));
    const fallen = companions.filter(c => stateById.get(c.id)?.status === "fallen");
    const injured = companions.filter(c => stateById.get(c.id)?.status === "injured");
    const survived = companions.filter(c => {
        const s = stateById.get(c.id);
        return !s || s.status === "active";
    });

    const headerAccent = fallen.length > 0 ? tokens.color.burgundy : tokens.color.moss;

    return (
        <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: tokens.easing.decelerate, delay: 0.2 }}
            className="max-w-[1040px] w-full mb-l"
            aria-label="원정 인원 회수록"
        >
            <div
                className="rounded-l p-l relative overflow-hidden"
                style={{
                    background: `radial-gradient(circle at 50% 0%, ${headerAccent}14 0%, transparent 58%), linear-gradient(180deg, ${tokens.color.ink}F0, ${tokens.color.ink}D4)`,
                    border: `1px solid ${headerAccent}55`,
                    boxShadow: `0 22px 48px ${tokens.color.ink}AA, inset 0 1px 0 ${headerAccent}22`,
                }}
            >
                {/* 2026-04-23: 좌측 accent 스트립 — Spirytale 언어 통일 (Toast/StoryFlash 와 동일). */}
                <div
                    aria-hidden
                    className="absolute inset-y-0 left-0"
                    style={{
                        width: 3,
                        background: `linear-gradient(180deg, ${headerAccent}FF 0%, ${headerAccent}44 100%)`,
                        boxShadow: `0 0 12px ${headerAccent}AA`,
                    }}
                />
                {/* 2026-04-23: accent dash + uppercase kicker. */}
                <div
                    className="font-body flex items-center gap-xs mb-s"
                    style={{
                        color: headerAccent,
                        fontSize: 11,
                        letterSpacing: "0.22em",
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            width: 14,
                            height: 1,
                            background: headerAccent,
                            boxShadow: `0 0 6px ${headerAccent}`,
                            opacity: 0.75,
                        }}
                    />
                    {fallen.length > 0 ? "장부의 마지막 줄" : "함께 닫는 장부"}
                </div>
                <motion.div
                    initial={{ opacity: 0, letterSpacing: "0.24em" }}
                    animate={{ opacity: 1, letterSpacing: "0.04em" }}
                    transition={{ duration: 0.9, ease: tokens.easing.decelerate, delay: 0.1 }}
                    className="font-display"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: "clamp(22px, 1.9vw, 30px)",
                        lineHeight: 1.2,
                        textShadow: `0 0 22px ${headerAccent}66, 0 2px 0 ${tokens.color.ink}`,
                    }}
                >
                    {fallen.length > 0
                        ? `원정 인원 ${companions.length}명 중 ${fallen.length}명의 이름이 먼저 장부에 남았다`
                        : `원정 인원 ${companions.length}명 전원이 장부를 함께 닫는다`}
                </motion.div>
                <motion.div
                    aria-hidden
                    initial={{ width: "8%", opacity: 0 }}
                    animate={{ width: "38%", opacity: 0.75 }}
                    transition={{ delay: 0.35, duration: 0.8, ease: tokens.easing.decelerate }}
                    className="mt-s mb-m"
                    style={{
                        height: 1,
                        background: `linear-gradient(90deg, ${headerAccent}FF, transparent)`,
                        boxShadow: `0 0 14px ${headerAccent}66`,
                    }}
                />

                {fallen.length > 0 && (
                    <div className="grid gap-s md:grid-cols-2 mb-m">
                        {fallen.map((c, idx) => {
                            const s = stateById.get(c.id);
                            return (
                                <FallenCard
                                    key={c.id}
                                    companion={c}
                                    state={s}
                                    maxWeeks={maxWeeks}
                                    delayMs={idx * 90}
                                />
                            );
                        })}
                    </div>
                )}

                {/* 살아 돌아온 / 부상 이름 간단 나열 — fallen 섹션 아래에 한 줄 요약 */}
                {(survived.length > 0 || injured.length > 0) && (
                    <div className="grid gap-s md:grid-cols-2">
                        {survived.length > 0 && (
                            <SurvivorStrip
                                title="끝내 돌아온 이름"
                                kicker="계속 숨 쉬는 이름"
                                accent={tokens.color.moss}
                                companions={survived}
                            />
                        )}
                        {injured.length > 0 && (
                            <SurvivorStrip
                                title="상처를 안고 돌아온 이름"
                                kicker="숨은 대가를 안은 이름"
                                accent={tokens.color.ember}
                                companions={injured}
                            />
                        )}
                    </div>
                )}
            </div>
        </motion.section>
    );
}

function FallenCard({
    companion,
    state,
    maxWeeks,
    delayMs,
}: {
    companion: ScenarioCompanion;
    state: ScenarioCompanionState | undefined;
    maxWeeks: number;
    delayMs: number;
}) {
    const fallenWeek = state?.lastChangedWeek ?? null;
    const weekLabel = fallenWeek
        ? `${fallenWeek}주차 · 전체 ${maxWeeks}주 중 ${Math.round((fallenWeek / maxWeeks) * 100)}%`
        : "쓰러진 때는 흐릿하다";

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: tokens.easing.decelerate, delay: delayMs / 1000 }}
            className="rounded-m p-m flex gap-m"
            style={{
                background: `linear-gradient(135deg, ${tokens.color.burgundy}22, ${tokens.color.ink}B4)`,
                border: `1px solid ${tokens.color.burgundy}55`,
                boxShadow: `inset 0 1px 0 ${tokens.color.burgundy}22`,
            }}
        >
            {/*
             * 초상 원형 프레임 — portraitSrc 있으면 이미지, 없으면 이름 이니셜 fallback.
             * fallen 톤 (burgundy glow) 으로 "추모" 감 전달.
             */}
            <div
                className="shrink-0 relative"
                style={{ width: 56, height: 56 }}
            >
                <div
                    className="absolute inset-0 rounded-full overflow-hidden"
                    style={{
                        background: `${tokens.color.ink}D8`,
                        border: `1px solid ${tokens.color.burgundy}88`,
                        boxShadow: `0 0 18px ${tokens.color.burgundy}55`,
                    }}
                >
                    {companion.portraitSrc ? (
                        <img
                            src={companion.portraitSrc}
                            alt=""
                            onError={(e) => {
                                const el = e.target as HTMLImageElement;
                                el.style.display = "none";
                            }}
                            className="h-full w-full"
                            style={{
                                objectFit: "cover",
                                filter: "grayscale(0.4) brightness(0.8)",
                            }}
                        />
                    ) : (
                        <div
                            className="h-full w-full flex items-center justify-center font-display"
                            style={{
                                color: tokens.color.burgundy,
                                fontSize: tokens.type.size.body,
                                letterSpacing: tokens.type.letterSpacing.wide,
                            }}
                        >
                            {companion.displayName.slice(0, 1)}
                        </div>
                    )}
                </div>
                {/* 추모 글리프 — 우측 하단에 작은 ✟ */}
                <div
                    aria-hidden
                    className="absolute rounded-full flex items-center justify-center"
                    style={{
                        right: -2,
                        bottom: -2,
                        width: 20,
                        height: 20,
                        background: `${tokens.color.ink}EE`,
                        border: `1px solid ${tokens.color.burgundy}`,
                        color: tokens.color.burgundy,
                        fontSize: 11,
                    }}
                >
                    ✟
                </div>
            </div>
            <div className="min-w-0 flex-1">
                {/* 2026-04-23: Fallen 카드에도 uppercase kicker — 추모 카드 톤 통일. */}
                <div
                    className="font-body"
                    style={{
                        color: tokens.color.burgundy,
                        fontSize: 13.5,
                        letterSpacing: "0.22em",
                        opacity: 0.85,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {companion.role ?? "원정 인원"}
                </div>
                <div
                    className="font-display"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        letterSpacing: "0.04em",
                        textShadow: `0 0 10px ${tokens.color.burgundy}55, 0 1px 0 ${tokens.color.ink}`,
                        marginTop: 1,
                    }}
                >
                    {companion.displayName}
                </div>
                <div
                    className="font-body"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 13.5,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        marginTop: 2,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {weekLabel}
                </div>
                <div
                    className="mt-xs font-body"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 13.5,
                        lineHeight: tokens.type.lineHeight.normal,
                        fontStyle: "italic",
                        opacity: 0.92,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {memorialLineFor(companion)}
                </div>
            </div>
        </motion.div>
    );
}

function SurvivorStrip({
    title,
    kicker,
    accent,
    companions,
}: {
    title: string;
    kicker: string;
    accent: string;
    companions: ScenarioCompanion[];
}) {
    return (
        <div
            className="rounded-m p-s relative overflow-hidden"
            style={{
                background: `linear-gradient(180deg, ${tokens.color.ink}B4, ${tokens.color.ink}88)`,
                border: `1px solid ${accent}44`,
                boxShadow: `inset 0 1px 0 ${accent}22`,
            }}
        >
            {/* 2026-04-23: accent dash + uppercase kicker — 회수록 내부도 Spirytale 톤 통일. */}
            <div
                className="font-body flex items-center gap-xs"
                style={{
                    color: accent,
                    fontSize: 13.5,
                    letterSpacing: "0.22em",
                    opacity: 0.9,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 10,
                        height: 1,
                        background: accent,
                        boxShadow: `0 0 6px ${accent}`,
                        opacity: 0.7,
                    }}
                />
                {kicker} · {companions.length}
            </div>
            <div
                className="font-display mb-xs mt-[2px]"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 13.5,
                    letterSpacing: "0.04em",
                    textShadow: `0 0 10px ${accent}44, 0 1px 0 ${tokens.color.ink}`,
                }}
            >
                {title}
            </div>
            <div className="flex flex-wrap gap-xs mt-xs">
                {companions.map(c => (
                    <span
                        key={c.id}
                        className="rounded-pill px-s py-[2px] font-body"
                        style={{
                            color: tokens.color.parchment,
                            background: `${tokens.color.ink}88`,
                            border: `1px solid ${accent}44`,
                            boxShadow: `inset 0 1px 0 ${accent}22`,
                            fontSize: 11,
                            letterSpacing: tokens.type.letterSpacing.wide,
                        }}
                    >
                        {c.displayName}
                    </span>
                ))}
            </div>
        </div>
    );
}

/**
 * 동료별 추모 한 줄. 현재는 role 기반 generic 라이브러리.
 * 콘텐츠 보강 시 companion id 기반 고유 문구로 교체 가능.
 */
function memorialLineFor(companion: ScenarioCompanion): string {
    const role = companion.role ?? "";
    switch (role) {
        case "부관":
            return "명령은 늘 짧았고 기록은 늘 길었다. 마지막 장부에도 그의 필체는 남아 있다.";
        case "보급장교":
            return "배급표 한 장이 여기서 마지막으로 접힌다. 그 줄에 남은 무게가 가장 오래 읽힌다.";
        case "정찰병":
            return "먼저 본 길 끝에서 먼저 무너졌다. 그가 마지막으로 읽은 지형은 아무도 다시 찾지 못한다.";
        case "깃발수":
            return "깃발은 끝내 놓지 않았다. 깃발 아래 남은 이름이 그의 몫을 대신 이어 간다.";
        case "의무관":
            return "붕대와 이름을 세던 손이 여기서 멈췄다. 살려낸 이름은 계속 숨을 쉬고, 그의 숨만 장부에 남는다.";
        default:
            return "이 이름은 이제 원정의 장부에서 가장 조용한 줄에 남는다.";
    }
}
